/*
 * Hero product slide — Old Salt Coffee Company
 *
 * Powers the shoppable product cards rendered by snippets/hero-product-slide.liquid:
 * resolves the selected variant from the option pills, keeps price/image/button in
 * sync, and makes the whole card a click target for the product page. Adding to
 * cart itself is left to the theme's <product-form> element.
 */

const HERO_PRODUCT_INTERACTION_EVENT = 'hero:product-interaction';

/* Mirrors the list in snippets/coffee-format-detect.liquid. Kept here as well
   because this card resolves variants client side, so the grouping has to be
   worked out from the embedded variant data rather than handed over by Liquid. */
const HERO_SINGLE_SERVE_TOKENS = ['kcup', 'k-cup', 'k cup', 'pod', 'single serve'];

if (!customElements.get('hero-product-card')) {
  customElements.define(
    'hero-product-card',
    class HeroProductCard extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.variants = this.parseVariants();
        this.optionCount = Number(this.dataset.optionCount) || 1;
        this.addLabel = this.dataset.addLabel || this.strings.addToCart;

        this.pills = Array.from(this.querySelectorAll('[data-hpc-pill]'));
        this.selects = Array.from(this.querySelectorAll('[data-hpc-select]'));
        this.optionsScroller = this.querySelector('.hpc-card__body');
        this.variantIdInput = this.querySelector('[data-hpc-variant-id]');
        this.submitButton = this.querySelector('[data-hpc-submit]');
        this.submitText = this.querySelector('[data-hpc-submit-text]');
        this.priceWrapper = this.querySelector('[data-hpc-price]');
        this.priceCurrent = this.querySelector('[data-hpc-price-current]');
        this.priceCompare = this.querySelector('[data-hpc-price-compare]');
        this.saleFlag = this.querySelector('[data-hpc-sale-flag]');
        this.variantLabel = this.querySelector('[data-hpc-variant-label]');
        this.image = this.querySelector('.hpc-card__img');

        this.formatIndex = this.dataset.formatIndex === undefined ? -1 : Number(this.dataset.formatIndex);
        this.sizeIndex = this.dataset.sizeIndex === undefined ? -1 : Number(this.dataset.sizeIndex);
        this.formatTabs = Array.from(this.querySelectorAll('[data-hpc-format-tab]'));
        if (this.hasFormatGroups) this.buildFormatGroups();

        this.pills.forEach((pill) => {
          pill.addEventListener('click', this.onPillClick.bind(this));
        });

        this.selects.forEach((select) => {
          select.addEventListener('change', this.onSelectChange.bind(this));
        });

        this.formatTabs.forEach((tab) => {
          tab.addEventListener('click', this.onFormatTabClick.bind(this));
        });

        this.addEventListener('click', this.onCardClick.bind(this));
        this.addEventListener('pointerdown', this.signalInteraction.bind(this));
        this.addEventListener('focusin', this.signalInteraction.bind(this));

        if (this.pills.length || this.selects.length) this.updateSelection();

        this.revealSelectedPills();
        this.watchOptionsOverflow();
      }

      /* Pill rows scroll sideways instead of wrapping, so the chosen value can
         start out past the right edge. Nudge each row so it opens on it. */
      revealSelectedPills() {
        this.querySelectorAll('.hpc-card__pills').forEach((row) => {
          const selected = row.querySelector('[aria-pressed="true"]');
          if (!selected) return;

          const overshoot = selected.getBoundingClientRect().right - row.getBoundingClientRect().right;
          if (overshoot > 0) row.scrollLeft += overshoot;
        });
      }

      /* The options area is what absorbs a short hero, so how much of it is
         hidden changes with the viewport, not just with scrolling. */
      watchOptionsOverflow() {
        if (!this.optionsScroller) return;

        const update = this.updateOptionsOverflow.bind(this);
        this.optionsScroller.addEventListener('scroll', update, { passive: true });
        new ResizeObserver(update).observe(this.optionsScroller);

        update();
      }

      /* Fades whichever edge has more options beyond it, so a scrollable list
         never looks like it simply ends. */
      updateOptionsOverflow() {
        const scroller = this.optionsScroller;
        const hidden = scroller.scrollHeight - scroller.clientHeight;

        if (hidden <= 1) {
          scroller.dataset.overflow = 'none';
          return;
        }

        const moreAbove = scroller.scrollTop > 1;
        const moreBelow = scroller.scrollTop < hidden - 1;

        if (moreAbove && moreBelow) {
          scroller.dataset.overflow = 'both';
        } else {
          scroller.dataset.overflow = moreAbove ? 'top' : 'bottom';
        }
      }

      get strings() {
        const variantStrings = window.variantStrings || {};
        return {
          addToCart: variantStrings.addToCart || 'Add to cart',
          soldOut: variantStrings.soldOut || 'Sold out',
          unavailable: variantStrings.unavailable || 'Unavailable',
        };
      }

      parseVariants() {
        const script = this.querySelector('script[data-hpc-variants]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent) || [];
        } catch (error) {
          console.error('hero-product-card: could not parse variant data', error);
          return [];
        }
      }

      /* The value chosen for each option, in the order the product defines them.
         An option is rendered either as pills or as a dropdown, never as both. */
      get selection() {
        const values = new Array(this.optionCount).fill(null);

        this.pills.forEach((pill) => {
          if (pill.getAttribute('aria-pressed') !== 'true') return;
          values[Number(pill.dataset.optionIndex)] = pill.dataset.optionValue;
        });

        this.selects.forEach((select) => {
          values[Number(select.dataset.optionIndex)] = select.value;
        });

        return values;
      }

      onSelectChange() {
        this.updateSelection();
        this.signalInteraction();
      }

      /* Bags and pods share no sizes, so the two ranges are shown as tabs and each
         one narrows the grind and size rows to what exists inside it. */
      get hasFormatGroups() {
        return this.formatIndex >= 0 && this.sizeIndex >= 0 && this.formatTabs.length > 0;
      }

      groupOf(formatValue) {
        const value = String(formatValue || '').toLowerCase();
        return HERO_SINGLE_SERVE_TOKENS.some((token) => value.includes(token)) ? 'single' : 'beans';
      }

      /* Built from the variants that exist rather than from the option lists, so a
         size only appears under the range it is actually sold in. */
      buildFormatGroups() {
        this.formatGroups = {
          beans: { formats: [], sizes: [] },
          single: { formats: [], sizes: [] },
        };

        this.variants.forEach((variant) => {
          const format = variant.options[this.formatIndex];
          const size = variant.options[this.sizeIndex];
          const group = this.formatGroups[this.groupOf(format)];

          if (!group.formats.includes(format)) group.formats.push(format);
          if (!group.sizes.includes(size)) group.sizes.push(size);
        });
      }

      onFormatTabClick(event) {
        const group = event.currentTarget.dataset.hpcFormatTab;

        // First in stock variant of the range, so Beans opens on its smallest bag
        // and Single Serve on its smallest count.
        const target =
          this.variants.find((variant) => variant.available && this.groupOf(variant.options[this.formatIndex]) === group) ||
          this.variants.find((variant) => this.groupOf(variant.options[this.formatIndex]) === group);

        if (!target) return;

        this.select(this.formatIndex, target.options[this.formatIndex]);
        this.select(this.sizeIndex, target.options[this.sizeIndex]);

        this.updateSelection();
        this.revealSelectedPills();
        this.signalInteraction();
      }

      select(optionIndex, value) {
        this.pills
          .filter((pill) => Number(pill.dataset.optionIndex) === optionIndex)
          .forEach((pill) => pill.setAttribute('aria-pressed', String(pill.dataset.optionValue === value)));

        this.selects
          .filter((select) => Number(select.dataset.optionIndex) === optionIndex)
          .forEach((select) => {
            select.value = value;
          });
      }

      /* Derived from the selection rather than remembered, so the tabs cannot drift
         out of step with the variant the card is about to add. */
      applyFormatGroup(selection) {
        if (!this.hasFormatGroups) return;

        const group = this.groupOf(selection[this.formatIndex]);
        const { formats, sizes } = this.formatGroups[group];

        this.formatTabs.forEach((tab) => {
          const active = tab.dataset.hpcFormatTab === group;
          tab.setAttribute('aria-pressed', String(active));
          tab.classList.toggle('hpc-card__tab--active', active);
        });

        this.limitRow(this.formatIndex, formats);
        this.limitRow(this.sizeIndex, sizes);

        // Pods come in one grind, so that row has nothing left to choose.
        const formatRow = this.querySelector(`[data-hpc-option-row="${this.formatIndex}"]`);
        if (formatRow) formatRow.classList.toggle('variant-value-hidden', formats.length < 2);
      }

      limitRow(optionIndex, allowed) {
        this.pills
          .filter((pill) => Number(pill.dataset.optionIndex) === optionIndex)
          .forEach((pill) => pill.classList.toggle('variant-value-hidden', !allowed.includes(pill.dataset.optionValue)));

        this.selects
          .filter((select) => Number(select.dataset.optionIndex) === optionIndex)
          .forEach((select) => {
            Array.from(select.options).forEach((option) => {
              option.hidden = !allowed.includes(option.value);
            });
          });
      }

      onPillClick(event) {
        const pill = event.currentTarget;
        const optionIndex = pill.dataset.optionIndex;

        this.pills
          .filter((candidate) => candidate.dataset.optionIndex === optionIndex)
          .forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate === pill)));

        this.updateSelection();
        this.signalInteraction();
      }

      updateSelection() {
        const selection = this.selection;
        const variant = this.variants.find((candidate) =>
          candidate.options.every((value, index) => value === selection[index])
        );

        this.applyFormatGroup(selection);
        this.markUnavailableOptions(selection);
        this.updatePrice(variant);
        this.updateImage(variant);
        this.updateVariantLabel(variant);
        this.updateSubmitButton(variant);
      }

      /* Keeps the line under the title honest about what add to cart will add. */
      updateVariantLabel(variant) {
        if (!this.variantLabel || !variant) return;
        this.variantLabel.textContent = variant.options.join(' · ');
      }

      /* Dims option values that cannot be combined with the current selection. */
      markUnavailableOptions(selection) {
        this.pills.forEach((pill) => {
          const optionIndex = Number(pill.dataset.optionIndex);
          const value = pill.dataset.optionValue;

          const reachable = this.variants.some(
            (candidate) =>
              candidate.available &&
              candidate.options.every((candidateValue, index) =>
                index === optionIndex ? candidateValue === value : candidateValue === selection[index]
              )
          );

          pill.dataset.unavailable = String(!reachable);
        });
      }

      updatePrice(variant) {
        if (!this.priceWrapper || !variant) return;

        if (this.priceCurrent) this.priceCurrent.innerHTML = variant.price;

        const onSale = Boolean(variant.compare_at_price);
        this.priceWrapper.classList.toggle('hpc-card__price--on-sale', onSale);

        if (this.priceCompare) {
          this.priceCompare.innerHTML = variant.compare_at_price || '';
          this.priceCompare.toggleAttribute('hidden', !onSale);
        }

        if (this.saleFlag) this.saleFlag.toggleAttribute('hidden', !onSale);
      }

      updateImage(variant) {
        if (!this.image || !variant || !variant.image) return;
        if (this.image.src === variant.image) return;

        this.image.removeAttribute('srcset');
        this.image.src = variant.image;
      }

      updateSubmitButton(variant) {
        if (!this.submitButton) return;

        const purchasable = Boolean(variant && variant.available);

        if (this.variantIdInput) {
          this.variantIdInput.value = variant ? variant.id : '';
          this.variantIdInput.disabled = !purchasable;
        }

        this.submitButton.toggleAttribute('disabled', !purchasable);
        this.submitButton.setAttribute('aria-disabled', String(!purchasable));

        if (!this.submitText) return;

        if (purchasable) {
          this.submitText.textContent = this.addLabel;
        } else if (variant) {
          this.submitText.textContent = this.strings.soldOut;
        } else {
          this.submitText.textContent = this.strings.unavailable;
        }
      }

      /* Treats the card as one big link, without stealing clicks from the controls. */
      onCardClick(event) {
        const url = this.dataset.productUrl;
        if (!url) return;
        if (event.target.closest('a, button, input, label, select, textarea, product-form')) return;

        const selection = window.getSelection();
        if (selection && selection.toString().length > 0) return;

        window.location.href = url;
      }

      /* Lets the hero carousel pause autoplay so the slide never moves mid-purchase. */
      signalInteraction() {
        this.dispatchEvent(new CustomEvent(HERO_PRODUCT_INTERACTION_EVENT, { bubbles: true }));
      }
    }
  );
}